import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MaskedLines } from "../components/Reveal";

export default function AuthPage({ mode }) {
  const isJoin = mode === "join";
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(isJoin ? "/auth/register" : "/auth/login", isJoin ? form : { email: form.email, password: form.password });
      setUser(data);
      toast.success(isJoin ? "Welcome to the Club. Prices unlocked." : "Welcome back. Prices unlocked.");
      navigate(location.state?.from || (data.role === "admin" ? "/admin" : "/"));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputCls = "w-full bg-transparent border border-[#27272A] px-4 py-3.5 text-sm outline-none focus:border-[#FFCC00] placeholder:text-white/30";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16 grid lg:grid-cols-2 gap-16 items-center">
      <div>
        <MaskedLines
          lines={isJoin ? ["JOIN THE", "UK JOBLOT", "CLUB."] : ["WELCOME", "BACK,", "TRADER."]}
          className="font-display text-6xl sm:text-7xl leading-[0.9]"
        />
        <div className="mt-10 space-y-4">
          {["Every member price unlocked instantly", "First dibs on daily hero deals", "Deal drop alerts to your inbox", "Free forever — no card required"].map((t, i) => (
            <div key={i} className="flex items-center gap-4 text-sm text-white/60">
              <span className="text-[#FFCC00] font-display">{String(i + 1).padStart(2, "0")}</span> {t}
            </div>
          ))}
        </div>
      </div>
      <div className="border border-[#27272A] p-8 sm:p-12 bg-[#111111]">
        <div className="hazard-stripes h-1.5 -mt-8 sm:-mt-12 -mx-8 sm:-mx-12 mb-8" />
        <h2 className="font-display text-3xl mb-8">{isJoin ? "CREATE FREE ACCOUNT" : "MEMBER LOGIN"}</h2>
        <form onSubmit={submit} className="space-y-5">
          {isJoin && (
            <input data-testid="auth-name-input" required placeholder="FULL NAME" value={form.name} onChange={set("name")} className={inputCls} />
          )}
          <input data-testid="auth-email-input" required type="email" placeholder="EMAIL ADDRESS" value={form.email} onChange={set("email")} className={inputCls} />
          <input data-testid="auth-password-input" required type="password" placeholder="PASSWORD (MIN 6 CHARS)" value={form.password} onChange={set("password")} className={inputCls} />
          {error && <div data-testid="auth-error-message" className="text-[#FF3B30] text-xs border border-[#FF3B30] p-3">{error}</div>}
          <button data-testid="auth-submit-button" disabled={busy} className="btn-industrial w-full py-4 text-xl">
            {busy ? "WORKING…" : isJoin ? "UNLOCK MEMBER PRICES" : "LOG IN"}
          </button>
        </form>
        <div className="text-xs text-white/40 mt-6 text-center">
          {isJoin ? (
            <>Already a member? <Link data-testid="switch-to-login-link" to="/login" className="text-[#FFCC00] hover:text-white">Log in</Link></>
          ) : (
            <>Not a member yet? <Link data-testid="switch-to-join-link" to="/join" className="text-[#FFCC00] hover:text-white">Join free</Link></>
          )}
        </div>
      </div>
    </div>
  );
}
